/*eslint-disable*/
import React, { useState, useContext, useMemo, useEffect } from "react"; // Added useEffect
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert // Added Alert for errors
} from "reactstrap";
import Select from "react-select";
import { DataContext } from "../context/DataContext";
import placeholder from "assets/img/placeholder.jpg";
import { createUser, updateUser } from "../context/api";
import { Avatar } from "../components/Avatar"; // Assuming Avatar is in components
import { API_BASE } from "../config/api";


const Users = () => {
  const { data, setData } = useContext(DataContext);
  const initialUsers = data.users || [];
  const [users, setUsers] = useState(initialUsers);
  const [hover, setHover] = useState(false);
  const isSuperuser = data.userProfile?.is_superuser === true;
  const [showArchived, setShowArchived] = useState(false);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState({ action: null, user: null });

  const [selectedUserId, setSelectedUserId] = useState(
    initialUsers.length > 0 ? initialUsers[0].id : null
  );

  const territories = data.territories || [];

  const getTerritoryNames = (ids) => {
    return ids
      .map((id) => {
        const territory = territories.find((t) => t.id === id);
        return territory?.name || `ID ${id}`;
      })
      .join(", ");
  };

  const territoryOptions = useMemo(() => {
    return (data.territories || [])
      .filter(t => !t.is_archived)
      .map(t => ({ value: t.id, label: t.name }));
  }, [data.territories]);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // State for S3 upload and image handling in modal
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [modalError, setModalError] = useState("");

  const accessLevelOptions = [
    { value: "admin", label: "Admin" },
    { value: "sales", label: "Sales" }
  ];

  // useEffect to update previewUrl when editingUser changes (e.g. modal opens)
  useEffect(() => {
    if (editingUser && editingUser.image) {
      setPreviewUrl(editingUser.image);
    } else if (editingUser) {
      setPreviewUrl(placeholder); // For new user or user with no image
    } else {
      setPreviewUrl(placeholder); // When modal is closed or no user editing
    }
  }, [editingUser]);


  const openEditUserModal = (user) => {
    setEditingUser({ ...user });
    // setPreviewUrl(user.image || placeholder); // Handled by useEffect
    setSelectedFile(null); // Reset selected file when opening modal for a new edit
    setModalError("");
    setUserModalOpen(true);
  };

  const openAddUserModal = () => {
    setEditingUser({
      id: null,
      name: "",
      title: "",
      email: "",
      distribution: "",
      image: "", // Will show placeholder via previewUrl from useEffect
      phone: "",
      area: {
        regions: [],
        territories: [],
        accessLevel: ""
      },
      is_archived: false
    });
    // setPreviewUrl(placeholder); // Handled by useEffect
    setSelectedFile(null); // Reset selected file for new user
    setModalError("");
    setUserModalOpen(true);
  };

  const closeUserModal = () => {
    setEditingUser(null);
    setSelectedFile(null);
    // setPreviewUrl(placeholder); // Let useEffect handle based on editingUser being null
    setModalError("");
    setIsSavingUser(false);
    setUserModalOpen(false);
  };

  const handleUserFieldChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({ ...prev, [name]: value }));
  };

  // --- S3 Image Upload Functions (adapted from UserProfile.js) ---
  function convertToPng(file) {
    return new Promise((resolve, reject) => {
      if (!file.type.match(/image\/(jpeg|jpg)/)) {
        return resolve(file); // Pass through if not JPEG/JPG
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const pngFile = new File(
                  [blob],
                  (file.name.replace(/\.[^/.]+$/, '') || 'image') + '.png',
                  { type: 'image/png' }
                );
                resolve(pngFile);
              } else {
                reject(new Error('Canvas to Blob conversion failed'));
              }
            },
            'image/png'
          );
        };
        img.onerror = (err) => reject(err);
        img.src = reader.result;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setModalError(""); // Clear previous errors

    convertToPng(file)
      .then((convFile) => {
        setSelectedFile(convFile); // Store the (potentially converted) file object
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result); // Update preview in modal
        };
        reader.readAsDataURL(convFile);
      })
      .catch((err) => {
        console.error('Image conversion or processing failed', err);
        setModalError('Failed to process image. Please try another one.');
        setSelectedFile(null);
        setPreviewUrl(editingUser?.image || placeholder); // Revert to original or placeholder
      });
  };
  
  async function uploadToS3(file, userId) {
    if (!userId) {
      // This case should ideally be handled before calling uploadToS3 for new users.
      // For robustness, we throw an error here if it's still called without a userId.
      throw new Error("User ID is required for S3 upload. Cannot upload image for a new user without an ID.");
    }
    const API = API_BASE;
    const res = await fetch(`${API}/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileType: file.type,
        folder: 'users',
        userId: String(userId) // Ensure userId is a string
      }),
    });
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`Failed to get upload URL: ${res.status} ${errorText}`);
    }
    const { uploadUrl, key } = await res.json();

    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    
    if (!uploadRes.ok) {
      const s3ErrorText = await uploadRes.text();
      console.error('S3 PUT failed with status:', uploadRes.status, 'Response:', s3ErrorText);
      throw new Error(`Failed to upload file to S3: ${uploadRes.status}. ${s3ErrorText}`);
    }

    const bucket = 'beveragebrothers'; // Consider making this an env variable
    return `https://${bucket}.s3.amazonaws.com/${key}`;
  }
  // --- End S3 Image Upload Functions ---


  const handleImageError = (e) => {
    e.target.src = placeholder;
  };

  const handleRegionChange = (selectedOptions) => {
    setEditingUser((prev) => ({
      ...prev,
      area: {
        ...prev.area,
        regions: selectedOptions ? selectedOptions.map((opt) => opt.value) : []
      }
    }));
  };

  // For updating the access level via react-select
  const handleAccessLevelChange = (selectedOption) => {
    setEditingUser((prev) => ({
      ...prev,
      role: selectedOption ? selectedOption.value : "",
    }));
  };

  // Handle territories input (comma-separated IDs)
  const handleTerritoriesChange = (e) => {
    const value = e.target.value;
    const territories = value
      .split(",")
      .map((v) => parseInt(v.trim()))
      .filter((v) => !isNaN(v));
    setEditingUser((prev) => ({
      ...prev,
      area: {
        ...prev.area,
        territories
      }
    }));
  };

const handleSaveUser = async () => {
  if (!editingUser) {
    console.error("No user defined.");
    setModalError("User data is incomplete.");
    return;
  }
  setIsSavingUser(true);
  setModalError("");

  let payload = { ...editingUser };
  const isEditing = Boolean(editingUser.id);
  const originalUsersState = [...data.users]; // Create a stable copy for rollback
  const originalTerritoriesState = [...data.territories]; // Create a stable copy for rollback

  try {
    let finalImageUrl = editingUser.image; // Start with current image or empty string

    if (selectedFile) {
      if (isEditing && editingUser.id) { // Only upload if editing an existing user with an ID
        const plainUrl = await uploadToS3(selectedFile, editingUser.id);
        finalImageUrl = `${plainUrl}?cb=${Date.now()}`; // Cache-busted URL
      } else if (!isEditing) {
        // For new users, S3 upload will not happen here. Image can be added after creation via edit.
        // The `payload.image` will remain as it was (likely empty or placeholder from `openAddUserModal`)
        // or could be set to `previewUrl` if it's a local data URL, but backend needs to handle that.
        // For simplicity, we'll let `payload.image` be what it is; S3 upload is for edits.
        console.warn("New user: Image selected but will not be uploaded to S3 until user is created and edited.");
        // If you want to save the local preview (data URL) for a new user, ensure backend supports it.
        // Otherwise, payload.image should be empty or a default placeholder.
        finalImageUrl = ""; // Or placeholder, if selectedFile was for a new user.
      }
    }
    payload.image = finalImageUrl; // Update payload with the final image URL

    // API call
    let savedUser;
    let tempNewUserId = null; // For new user optimistic update

    if (isEditing) {
      savedUser = await updateUser(payload.id, payload);
    } else {
      tempNewUserId = Date.now() + Math.random(); // Temporary ID for optimistic update
      const createPayload = { ...payload, id: tempNewUserId }; // Use temp ID for local state
      // Send payload without temp ID to backend, as backend generates the real ID
      const apiCreatePayload = { ...payload };
      delete apiCreatePayload.id; 
      savedUser = await createUser(apiCreatePayload);
    }

        // ─── RE-INJECT the edited territories ───────────────────────────
    savedUser.area = savedUser.area || {};
    savedUser.area.territories = payload.area.territories;

    // Optimistic update & Reconciliation
    let updatedUsersList = originalUsersState.map(u => 
        (isEditing && u.id === savedUser.id) ? savedUser :
        (!isEditing && u.id === tempNewUserId) ? savedUser : // Replace temp user with saved user
        u
    );
    if (!isEditing && !originalUsersState.find(u => u.id === savedUser.id)) {
        // If the new user wasn't matched by tempId (e.g. if tempId logic was skipped for some reason)
        // ensure it's added. This also handles the case where tempNewUserId was not used in mapping.
        const userExists = updatedUsersList.some(u => u.id === savedUser.id);
        if (!userExists) {
             updatedUsersList = [...originalUsersState, savedUser];
        } else {
            updatedUsersList = updatedUsersList.map(u => u.id === tempNewUserId ? savedUser : u);
        }
    }
    // If a new user was created, ensure it's in the list, replacing any temp version
    if (!isEditing) {
        const existingUserIndex = updatedUsersList.findIndex(u => u.id === tempNewUserId);
        if (existingUserIndex !== -1) {
            updatedUsersList[existingUserIndex] = savedUser;
        } else {
            updatedUsersList.push(savedUser); // Add if not found (should be rare with tempId logic)
        }
    }

    // Update territories based on the final user ID (especially important for new users)
    const finalUserId = savedUser.id;
    let updatedTerritoriesList = originalTerritoriesState.map(t => ({
      ...t,
      assignReps: (t.assignReps || []).filter(rId => rId !== (isEditing ? finalUserId : tempNewUserId))
    }));
    (savedUser.area?.territories || []).forEach(tid => {
      const territoryIndex = updatedTerritoriesList.findIndex(t => t.id === tid);
      if (territoryIndex !== -1) {
        if (!updatedTerritoriesList[territoryIndex].assignReps.includes(finalUserId)) {
          updatedTerritoriesList[territoryIndex].assignReps.push(finalUserId);
        }
      }
    });

    setData(d => ({ ...d, users: updatedUsersList, territories: updatedTerritoriesList }));
    setUsers(updatedUsersList);
    
    closeUserModal();

  } catch (err) {
    console.error("Failed to save user:", err);
    setModalError(err.message || "Failed to save user. Please try again.");
    // No explicit rollback here as we're setting from original state at start of try
    // but if intermediate setData calls were made, they'd need rollback.
    // For safety, could re-set from original states if error occurs after optimistic update
    setData(d => ({ ...d, users: originalUsersState, territories: originalTerritoriesState }));
    setUsers(originalUsersState);
  } finally {
    setIsSavingUser(false);
  }
};


  const openConfirmModal = (action, user) => {
    setActionToConfirm({ action, user });
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setActionToConfirm({ action: null, user: null });
    setConfirmModalOpen(false);
  };

const executeConfirmedAction = async () => {
  const { user: userToUpdate, action } = actionToConfirm || {};
  if (!userToUpdate || !action) return;

  const newArchivedState = action === "archive";
  const updatedUserPayload = { ...userToUpdate, is_archived: newArchivedState };

  const originalUsersForArchive = [...users]; // Save state before optimistic update

  // Optimistically update UI
  const optimisticallyUpdatedUsers = users.map(u =>
    u.id === userToUpdate.id ? updatedUserPayload : u
  );
  setUsers(optimisticallyUpdatedUsers);
  setData(prev => ({
    ...prev,
    users: prev.users.map(u =>
      u.id === userToUpdate.id ? updatedUserPayload : u
    )
  }));

  closeConfirmModal();
  closeUserModal();
  // Do not closeUserModal here as it might not be open, or might be for a different user

  try {
    await updateUser(userToUpdate.id, { is_archived: newArchivedState }); // Send only the changed field
  } catch (err) {
    console.error("Failed to update user archived state:", err);
    // Rollback on failure
    setUsers(originalUsersForArchive);
    setData(prev => ({ ...prev, users: originalUsersForArchive }));
    // Optionally, show an error message to the user
  }
};

  const handleArchiveUser = () => {
    if (editingUser) openConfirmModal('archive', editingUser);
  };

  const handleRestoreUser = () => {
    if (editingUser) openConfirmModal('restore', editingUser);
  };

  const toggleShowArchived = () => {
    setShowArchived(prev => !prev);
  };
  
  const displayedUsers = users.filter(user => {
    // Hide test users
    if (user.email === 'admin@beveragebrothers.co.nz' || user.email === 'sales@beveragebrothers.co.nz') {
      return false;
    }
    return showArchived ? true : !user.is_archived;
  });

  return (
    <div className="content">
      {/* Header with page title and Add User button */}
      <Row className="align-items-center mb-3">
        <Col md="auto">
          <h2>Users</h2>
        </Col>
        <Col md="auto">
          <FormGroup check className="d-flex align-items-center h-100 mb-0"> {/* mb-0 to remove bottom margin from FormGroup */}
            <Label 
              check 
              className={`p-2 rounded ${showArchived ? "bg-secondary text-white" : "bg-light text-dark"}`} 
              style={{ cursor: 'pointer' }}
              htmlFor="showArchivedToggle" // Added htmlFor for accessibility
            >
              <Input 
                type="checkbox" 
                id="showArchivedToggle" // Added id for accessibility
                checked={showArchived} 
                onChange={toggleShowArchived} 
              />{' '}
              {showArchived ? "Hide Archived" : "Show Archived"}
            </Label>
          </FormGroup>
        </Col>
        {isSuperuser && (
                       <Col
  md="4"
  className="ml-auto" // Bootstrap class for left margin auto
  style={{
    cursor: "pointer",
    transition: "background-color 0.3s",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    borderRadius: "0.25rem",
    backgroundColor: hover ? "#f8f9fa" : "transparent"
  }}
  onClick={openAddUserModal}
  onMouseEnter={() => setHover(true)}
  onMouseLeave={() => setHover(false)}
>
  <i
    className="nc-icon nc-circle-10"
    style={{ fontSize: "1.5rem" }}
    title="Add New Product"
  />
  <p
    style={{
      textAlign: "center",
      marginTop: "0.25rem",
      textTransform: "none",
      margin: 0
    }}
  >
    Add New User
  </p>
</Col>
)}
      </Row>

      {/* Users Grid */}
      <Row>
        {displayedUsers.map((user) => (
          <Col key={user.id} xs="12" sm="6" md="4" lg="3" className="mb-3">
            <Card
              className="h-100" // Added h-100 for uniform height
              onClick={() => !user.is_archived || isSuperuser ? openEditUserModal(user) : null}
              style={{ cursor: (!user.is_archived || isSuperuser) ? "pointer" : "default", opacity: user.is_archived ? 0.6 : 1 }}
            >
              <CardBody className="text-center">
                <img
                  src={user.image || placeholder}
                  alt={user.name}
                  className="rounded-circle mb-2"
                  style={{
                    width: "80px",
                    height: "80px",
                    objectFit: "cover",
                    objectPosition: "top"
                  }}
                  onError={handleImageError}
                />
                <h5>{user.name}</h5>
                <p className="mb-1">{user.title}</p>
                <p className="mb-1">
                  <small>{user.email}</small>
                </p>
                <p className="mb-1">
                  <strong>Distribution:</strong> {user.distribution}
                </p>
                <p className="mb-1">
                  <strong>Regions:</strong> {(user.area?.regions || []).join(", ")}
                </p>
                <p className="mb-1">
                  <strong>Territories:</strong>{' '}
                  {getTerritoryNames(user.area?.territories || [])}
                </p>
                <p className="mb-0">
                  <strong>Access Level:</strong> {user.role ?? '—'}
                </p>
                {user.is_archived && <p className="text-danger mb-0"><strong>Archived</strong></p>}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* User Add/Edit Modal */}
      <Modal isOpen={userModalOpen} toggle={closeUserModal}>
        <ModalHeader toggle={closeUserModal}>
          {editingUser && editingUser.id ? "Edit User" : "Add New User"}
        </ModalHeader>
        <ModalBody>
          {modalError && <Alert color="danger" className="mb-2">{modalError}</Alert>}
          {editingUser && (
            <>
              <FormGroup className="text-center">
                <label htmlFor="userImageUploadModalInput" style={{ cursor: isSuperuser && !isSavingUser ? 'pointer' : 'default' }}>
                  <Avatar
                    src={previewUrl} 
                    alt={editingUser.name || "User image"}
                    size={100}
                    className="avatar border-gray mb-2"
                  />
                  {isSuperuser && !isSavingUser && <div>Click to change image</div>}
                </label>
                {isSuperuser && (
                  <Input
                    type="file"
                    id="userImageUploadModalInput" 
                    onChange={handleImageUpload}
                    accept="image/*"
                    hidden
                    disabled={isSavingUser}
                  />
                )}
              </FormGroup>
              <FormGroup>
                <Label for="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  disabled={!isSuperuser}
                  value={editingUser.name}
                  onChange={handleUserFieldChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="title">Title</Label>
                <Input
                  type="text"
                  id="title"
                  name="title"
                  disabled={!isSuperuser}
                  value={editingUser.title}
                  onChange={handleUserFieldChange}
                />
              </FormGroup>
                            <FormGroup>
                <Label for="phone">Phone</Label>
                <Input
                  type="text"
                  id="phone"
                  name="phone"
                  disabled={!isSuperuser}
                  value={editingUser.phone}
                  onChange={handleUserFieldChange}
                />
              </FormGroup>
              <FormGroup>
                <Label for="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={editingUser.email}
                  disabled={!!(editingUser && editingUser.id)} // Disabled only if editing an existing user
                  onChange={handleUserFieldChange}
                />
              </FormGroup>
              {/* <FormGroup>
                <Label for="distribution">Distribution</Label>
                <Select
                  id="distribution"
                  name="distribution"
                  value={distributionOptions.find(
                    (option) => option.value === editingUser.distribution
                  )}
                  onChange={(option) =>
                    setEditingUser((prev) => ({
                      ...prev,
                      distribution: option.value
                    }))
                  }
                  options={distributionOptions}
                  placeholder="Select Distribution"
                />
              </FormGroup> */}

              {/* <FormGroup>
                <Label for="regions">Regions</Label>
                <Select
                  isMulti
                  id="regions"
                  name="regions"
                  value={regionOptions.filter((opt) =>
                    editingUser.area.regions.includes(opt.value)
                  )}
                  onChange={handleRegionChange}
                  options={regionOptions}
                  placeholder="Select Regions"
                />
              </FormGroup> */}
<FormGroup>
  <Label for="territories">Territories</Label>
  <Select
    isMulti
    id="territories"
    name="territories"
    value={territoryOptions.filter(opt =>
      (editingUser.area?.territories || []).includes(opt.value) // Ensure access to territories is safe
    )}
    onChange={(selectedOptions) =>
      setEditingUser(prev => ({
        ...prev,
        area: {
          ...prev.area,
          territories: selectedOptions ? selectedOptions.map(opt => opt.value) : [] // Default to empty array if null
        }
      }))
    }
    options={territoryOptions}
    isDisabled={!isSuperuser}
    placeholder="Select Territories"
  />
</FormGroup>
              <FormGroup>
                <Label for="accessLevel">Access Level</Label>
                <Select
                  id="accessLevel"
                  name="accessLevel"
                  value={accessLevelOptions.find(
                    (option) => option.value === editingUser.role
                  )}
                  isDisabled={!isSuperuser}
                  onChange={handleAccessLevelChange}
                  options={accessLevelOptions}
                  placeholder="Select Access Level"
                  
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter style={{ padding: '0.75rem 1rem' }}>
           {editingUser && editingUser.id && !editingUser.is_archived && isSuperuser && (
            <Button color="warning" onClick={handleArchiveUser} disabled={isSavingUser} style={{ marginRight: "auto" }}>
              Archive User
            </Button>
          )}
          {editingUser && editingUser.is_archived && isSuperuser && (
            <Button color="success" onClick={handleRestoreUser} disabled={isSavingUser} style={{ marginRight: "auto" }}>
              Restore User
            </Button>
          )}
          <Button color="secondary" onClick={closeUserModal} disabled={isSavingUser}>
            Cancel
          </Button>
          {isSuperuser && (
          <Button color="primary" onClick={handleSaveUser} disabled={isSavingUser}>
            {isSavingUser ? (<><i className="fa fa-spinner fa-spin" /> Saving...</>) : (editingUser && editingUser.id ? "Save Changes" : "Add User")}
          </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal for Archive/Restore */}
      <Modal isOpen={confirmModalOpen} toggle={closeConfirmModal} centered>
        <ModalHeader toggle={closeConfirmModal}>
          Confirm {actionToConfirm.action === 'archive' ? 'Archive' : 'Restore'} User
        </ModalHeader>
        <ModalBody>
          Are you sure you want to {actionToConfirm.action} the user "{actionToConfirm.user?.name}"?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeConfirmModal}>Cancel</Button>
          <Button 
            color={actionToConfirm.action === 'archive' ? 'danger' : 'success'} 
            onClick={executeConfirmedAction}
          >
            Yes, {actionToConfirm.action === 'archive' ? 'Archive' : 'Restore'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Users;
